/**
 * SignalR service for real-time WebSocket communication.
 * Implements singleton pattern with ref-counting for connection management.
 * Handles automatic reconnection, error recovery, and message queueing.
 * Used for GPS tracking, file upload notifications, and real-time updates.
 */
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  IHttpConnectionOptions,
  HubConnectionState
} from '@microsoft/signalr';

import { LoggerService } from './LoggerService';

export interface SignalRConfig {
  url: string;
  automaticReconnect?: boolean | number[];
  transport?: HttpTransportType;
  logLevel?: LogLevel;
  withCredentials?: boolean;
  headers?: Record<string, string>;
  accessTokenFactory?: () => string | Promise<string>;
  serverTimeoutInMilliseconds?: number;
}

export interface SignalRMessage {
  method: string;
  args: unknown[];
}

export interface SignalRConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  isReconnecting: boolean;
  connectionId?: string;
  error?: string;
}

export type SignalRMessageHandler = (...args: unknown[]) => void;

export class SignalRService {
  private static instance: SignalRService | null = null;
  private connection: HubConnection | null = null;
  private config: SignalRConfig;
  private logger: LoggerService;
  private messageHandlers: Map<string, Set<SignalRMessageHandler>> = new Map();
  /** Hub methods with an active `connection.on` listener on the current socket. */
  private hubListenersAttached: Set<string> = new Set();
  private connectionStateListeners: Set<(state: SignalRConnectionState) => void> = new Set();
  private currentState: SignalRConnectionState = {
    isConnected: false,
    isConnecting: false,
    isReconnecting: false
  };
  private lastKnownConnectionId: string | undefined;
  private initializationPromise: Promise<void> | null = null;
  private connectPromise: Promise<void> | null = null;
  // Global consumer tracking to avoid start/stop thrashing across multiple hook instances
  private static activeConsumers = 0;
  private static pendingDisconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: SignalRConfig) {
    this.config = config;
    this.logger = new LoggerService('SignalRService');
  }

  /**
   * Get singleton instance of SignalRService
   * Ensures only one connection is maintained across the application
   */
  public static getInstance(config: SignalRConfig): SignalRService {
    if (!SignalRService.instance) {
      SignalRService.instance = new SignalRService(config);
    } else {
      // Update config if it has changed
      SignalRService.instance.updateConfig(config);
    }
    return SignalRService.instance;
  }

  /**
   * Reset singleton instance (useful for testing or reconfiguration)
   */
  public static resetInstance(): void {
    if (SignalRService.instance) {
      SignalRService.instance.disconnect().catch(() => {
        // Ignore disconnect errors during reset
      });
      SignalRService.instance = null;
    }
  }

  /**
   * Acquire a shared SignalR connection usage slot.
   * Cancels any pending disconnect.
   */
  public acquire(): void {
    SignalRService.activeConsumers += 1;
    if (SignalRService.pendingDisconnectTimer) {
      clearTimeout(SignalRService.pendingDisconnectTimer);
      SignalRService.pendingDisconnectTimer = null;
    }
  }

  /**
   * Release a shared SignalR connection usage slot.
   * Schedules disconnect when usage drops to zero.
   */
  public release(): void {
    SignalRService.activeConsumers = Math.max(0, SignalRService.activeConsumers - 1);
    if (SignalRService.activeConsumers === 0) {
      if (SignalRService.pendingDisconnectTimer) {
        clearTimeout(SignalRService.pendingDisconnectTimer);
      }
      SignalRService.pendingDisconnectTimer = setTimeout(() => {
        // Only disconnect if still no consumers
        if (SignalRService.activeConsumers === 0) {
          this.disconnect().catch(() => {
            /* swallow */
          });
        }
      }, 1200);
    }
  }

  /**
   * Update configuration (useful when config changes)
   */
  public updateConfig(newConfig: SignalRConfig): void {
    this.config = newConfig;
    // Reset initialization promise so new config is used
    this.initializationPromise = null;
  }

  /**
   * Initialize the SignalR connection
   */
  public async initialize(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      // this.logger.info('Initializing SignalR connection', { url: this.config.url });

      // Validate URL
      if (!this.config.url) {
        throw new Error('SignalR URL is required');
      }

      const connectionOptions: IHttpConnectionOptions = {
        transport: this.config.transport || HttpTransportType.WebSockets,
        withCredentials: this.config.withCredentials || false,
        headers: this.config.headers || {}
      };

      // Add authentication if provided
      if (this.config.accessTokenFactory) {
        connectionOptions.accessTokenFactory = this.config.accessTokenFactory;
      }

      const builder = new HubConnectionBuilder()
        .withUrl(this.config.url, connectionOptions)
        .configureLogging(this.config.logLevel || LogLevel.Information);

      // Handle automatic reconnection configuration
      if (this.config.automaticReconnect !== undefined) {
        if (typeof this.config.automaticReconnect === 'boolean') {
          builder.withAutomaticReconnect();
        } else {
          builder.withAutomaticReconnect(this.config.automaticReconnect);
        }
      } else {
        builder.withAutomaticReconnect();
      }

      this.connection = builder.build();

      // Set server timeout if provided
      if (this.config.serverTimeoutInMilliseconds) {
        this.connection.serverTimeoutInMilliseconds = this.config.serverTimeoutInMilliseconds;
      }

      this.setupConnectionEvents();

      // Don't auto-connect during initialization, let the user call connect() explicitly
      // This prevents connection attempts when the URL might not be available
    } catch (error) {
      this.logger.error('Failed to initialize SignalR connection', error);
      this.initializationPromise = null;
      throw error;
    }
  }

  /**
   * Connect to the SignalR hub
   */
  public async connect(): Promise<void> {
    if (!this.connection) {
      throw new Error('SignalR connection not initialized');
    }

    if (this.connection.state === HubConnectionState.Connected) {
      this.bindAllHubListeners();
      return;
    }

    if (this.connectPromise) {
      await this.connectPromise;
      this.bindAllHubListeners();
      return;
    }

    if (this.connection.state !== HubConnectionState.Disconnected) {
      this.logger.info('SignalR connection already connected or connecting');
      this.bindAllHubListeners();
      return;
    }

    this.connectPromise = this.performConnect();

    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async performConnect(): Promise<void> {
    if (!this.connection) {
      throw new Error('SignalR connection not initialized');
    }

    if (this.connection.state === HubConnectionState.Connected) {
      this.bindAllHubListeners();
      return;
    }

    if (this.connection.state !== HubConnectionState.Disconnected) {
      return;
    }

    try {
      this.updateState({ isConnecting: true, error: undefined });

      await new Promise((resolve) => setTimeout(resolve, 100));

      if (this.connection.state !== HubConnectionState.Disconnected) {
        this.updateState({ isConnecting: false });
        this.bindAllHubListeners();
        return;
      }

      await this.connection.start();

      const connectionId = this.connection.connectionId;
      this.lastKnownConnectionId = connectionId || undefined;

      this.updateState({
        isConnected: true,
        isConnecting: false,
        connectionId: connectionId || undefined
      });

      this.bindAllHubListeners();
    } catch (error) {
      this.logger.error('Failed to connect to SignalR hub', error);
      this.updateState({
        isConnected: false,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Disconnect from the SignalR hub
   */
  public async disconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      this.logger.info('Disconnecting from SignalR hub');
      // Avoid calling stop when already disconnected or never started
      if (this.connection.state === HubConnectionState.Disconnected) {
        // this.logger.debug('SignalR already disconnected; skipping stop()');
      } else {
        await this.connection.stop();
      }
      this.connection = null;
      this.initializationPromise = null;
      this.connectPromise = null;
      this.hubListenersAttached.clear();
      this.updateState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        connectionId: undefined,
        error: undefined
      });
    } catch (error) {
      this.logger.error('Error disconnecting from SignalR hub', error);
      throw error;
    }
  }

  /**
   * Send a message to the hub
   */
  public async invoke(method: string, ...args: unknown[]): Promise<unknown> {
    if (!this.connection) {
      throw new Error('SignalR connection not established');
    }

    if (!this.currentState.isConnected) {
      throw new Error('SignalR connection is not connected');
    }

    try {
      this.logger.debug('Invoking SignalR method', { method, args });
      return await this.connection.invoke(method, ...args);
    } catch (error) {
      this.logger.error('Failed to invoke SignalR method', { method, error });
      throw error;
    }
  }

  /**
   * Send a message to the hub without waiting for a response
   */
  public async send(method: string, ...args: unknown[]): Promise<void> {
    if (!this.connection) {
      throw new Error('SignalR connection not established');
    }

    if (!this.currentState.isConnected) {
      throw new Error('SignalR connection is not connected');
    }

    try {
      this.logger.debug('Sending SignalR message', { method, args });
      await this.connection.send(method, ...args);
    } catch (error) {
      this.logger.error('Failed to send SignalR message', { method, error });
      throw error;
    }
  }

  /**
   * Subscribe to a hub method
   */
  public on(method: string, handler: SignalRMessageHandler): void {
    if (!this.messageHandlers.has(method)) {
      this.messageHandlers.set(method, new Set());
    }

    this.messageHandlers.get(method)!.add(handler);
    this.ensureHubListener(method);
  }

  /**
   * Unsubscribe from a hub method
   */
  public off(method: string, handler?: SignalRMessageHandler): void {
    if (handler) {
      const handlers = this.messageHandlers.get(method);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(method);
          this.hubListenersAttached.delete(method);
          this.connection?.off(method);
        }
      }
    } else {
      this.messageHandlers.delete(method);
      this.hubListenersAttached.delete(method);
      this.connection?.off(method);
    }
  }

  /**
   * Subscribe to connection state changes
   */
  public onConnectionStateChange(listener: (state: SignalRConnectionState) => void): void {
    this.connectionStateListeners.add(listener);
    // Immediately call with current state
    listener(this.currentState);
  }

  /**
   * Unsubscribe from connection state changes
   */
  public offConnectionStateChange(listener: (state: SignalRConnectionState) => void): void {
    this.connectionStateListeners.delete(listener);
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): SignalRConnectionState {
    return { ...this.currentState };
  }

  /**
   * Check if connection is established
   */
  public isConnected(): boolean {
    return this.currentState.isConnected;
  }

  private setupConnectionEvents(): void {
    if (!this.connection) {
      return;
    }

    this.connection.onclose((error) => {
      const closedConnectionId = this.lastKnownConnectionId;
      this.logger.warn('SignalR connection closed', {
        error: error?.message,
        connectionId: closedConnectionId,
        reason: error?.name,
        errorType: error?.constructor.name,
        errorStack: error?.stack
      });
      this.updateState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: false,
        connectionId: closedConnectionId, // Preserve the last known connection ID
        error: error?.message
      });
    });

    this.connection.onreconnecting((error) => {
      this.logger.info('SignalR reconnecting', {
        error: error?.message,
        connectionId: this.connection?.connectionId
      });
      this.updateState({
        isConnected: false,
        isConnecting: false,
        isReconnecting: true,
        error: error?.message
      });
    });

    this.connection.onreconnected((connectionId) => {
      this.lastKnownConnectionId = connectionId;
      this.logger.info('SignalR reconnected', {
        connectionId,
        previousConnectionId: this.currentState.connectionId
      });
      this.hubListenersAttached.clear();
      this.bindAllHubListeners();
      this.updateState({
        isConnected: true,
        isConnecting: false,
        isReconnecting: false,
        connectionId,
        error: undefined
      });
    });
  }

  private ensureHubListener(method: string): void {
    if (!this.connection || this.hubListenersAttached.has(method)) {
      return;
    }

    this.hubListenersAttached.add(method);
    this.connection.on(method, (...args: unknown[]) => {
      this.logger.debug('Received SignalR message', { method, args });
      const handlers = this.messageHandlers.get(method);
      if (handlers) {
        handlers.forEach((registeredHandler) => {
          try {
            registeredHandler(...args);
          } catch (error) {
            this.logger.error('Error in SignalR message handler', { method, error });
          }
        });
      } else {
        this.logger.warn('No handlers registered for SignalR method', { method, args });
      }
    });
  }

  private bindAllHubListeners(): void {
    for (const method of this.messageHandlers.keys()) {
      this.ensureHubListener(method);
    }
  }

  private updateState(partialState: Partial<SignalRConnectionState>): void {
    this.currentState = { ...this.currentState, ...partialState };

    // Update connection state from actual connection if available
    if (this.connection) {
      this.currentState.isConnected = this.connection.state === HubConnectionState.Connected;
      this.currentState.isConnecting = this.connection.state === HubConnectionState.Connecting;
      this.currentState.isReconnecting = this.connection.state === HubConnectionState.Reconnecting;
      this.currentState.connectionId = this.connection.connectionId || undefined;
    }

    // Notify all listeners
    this.connectionStateListeners.forEach((listener) => {
      try {
        listener(this.currentState);
      } catch (error) {
        this.logger.error('Error in connection state listener', error);
      }
    });
  }
}
